<template>
  <PageDynamic path="/blogs" :title="posts?.title">
    <section class="section section--no-banner" v-if="posts">
      <div class="section__content bg-gray">
        <div class="container">
          <div class="row">
            <div class="col-lg-12">
              <div class="block block--blog">
                <div class="block__date" v-text="posts.date"></div>
                <h1 class="sr-only" v-text="posts.title"></h1>
                <h5 class="block__title" v-html="posts.title"></h5>
                <div class="block__image">
                  <img :src="posts.image" :alt="posts.title" />
                </div>
                <div class="block__content" v-html="posts.body"></div>

                <div class="block__gallery" v-if="posts.galleries && posts.galleries.length">
                  <div class="">
                    <Swiper class="swiper--blog-gallery" :modules="[SwiperNavigation]" :navigation="{
                      nextEl: '.swiper-button-next',
                      prevEl: '.swiper-button-prev',
                    }" :allowTouchMove="true" :slides-per-view="3.2" :spaceBetween="24" :breakpoints="{
                    920: {
                      slidesPerView: 3.2,
                      spaceBetween: 20,
                    },
                    768: {
                      slidesPerView: 2,
                      spaceBetween: 20,
                    },
                    400: {
                      slidesPerView: 2,
                      spaceBetween: 10,
                    },
                  }">
                      <SwiperSlide class="swiper-slide--custom" v-for="(slide, i) in posts.galleries" :key="i"
                        @click="() => showImg(i)">
                        <img :src="slide.path" :alt="slide.caption" />
                      </SwiperSlide>

                      <div class="swiper-navigation">
                        <div class="swiper-button-prev"></div>
                        <div class="swiper-button-next"></div>
                      </div>

                    </Swiper>

                    <VueEasyLightbox :visible="visibleRef" :imgs="posts.galleries.map((item) => item.path)"
                      :index="indexRef" @hide="onHide" />

                  </div>
                </div>
                
                  <NuxtLink class="btn btn--text" :to="localePath('/blogs')" target="_self">
                        <span class="btn__icon"><i class="icon-arrow-button-left"></i></span>
                        <span class="btn__title">{{ translate('back') }}</span>
                  </NuxtLink>
              </div>
            </div>
          </div>
        </div>
        <div class="social-share-wrapper">
          <Socialshare class="flex-column" />
        </div>
      </div>
    </section>
  </PageDynamic>
</template>

<script setup>

import { usePostServiceStore } from '@/stores/postService';
import { useTranslationStore } from "@/stores/translations";
import { useBreadcrumbSchema } from "~/composables/useBreadcrumbSchema";

import Socialshare from "~/components/apps/Socialshare.vue";
import PageDynamic from "@/components/pages/Dynamic.vue";
import { onMounted } from 'vue';

const route = useRoute();
const { public: { siteUrl } } = useRuntimeConfig();

const translationStore = useTranslationStore();
const translate = translationStore.translate;

const visibleRef = ref(false);
const indexRef = ref(0);

// Initialize the store and route
const postStore = usePostServiceStore();

// Compute the page title, content, and loading state from the store
const postID = route.params.id;

const posts = computed(() => {
  return postStore.posts;
});

const custom_head = computed(() => {
  const post = postStore.posts;
  return post.custom_head;
});

const custom_footer = computed(() => {
  const post = postStore.posts;
  return post.custom_footer;
});

const fetchServiceData = async (id) => {

  try {

    await postStore.fetchBlogByID(id[0]);

  } catch (error) {

    console.error("Error fetching page content:", error);
  }


};

// Inject custom head and footer for Client-Side only
const injectCustomHead = async (customHead) => {
  if (customHead) {
    const head = document.head;
    const headFragment = document.createRange().createContextualFragment(customHead);
    head.prepend(headFragment);
  }
};

const injectCustomFooter = async (customFooter) => {
  if (customFooter) {
    const body = document.body;
    const footerFragment = document.createRange().createContextualFragment(customFooter);
    body.appendChild(footerFragment);
  }
};

// Await the initial fetch at setup so posts.title is present in the
// server-rendered HTML (previously only fetched inside a fire-and-forget
// watch callback with `immediate: true`, so it never resolved before SSR
// serialized the page — leaving the H1/title empty for non-JS crawlers).
await fetchServiceData(route.params.id);

// Refetch when navigating between blog posts client-side (SPA nav).
watch(() => route.params.id, fetchServiceData);

const pageUrl = computed(() => `${siteUrl}${route.path}`);

useHead(() => {
  // postService sets posts = [] (truthy but title-less) when the fetch
  // fails or the id doesn't match a real post — same soft-404 shape as the
  // job posting pages (Auto-Fix 86eygyqqz tracks the underlying 404 fix
  // separately). Emitting Article/BreadcrumbList schema for a page with no
  // real article content would fabricate data Google can't verify against
  // what's on the page, so skip entirely rather than guess.
  const post = posts.value;
  if (!post?.title) return { script: [] };

  // PostResource has no CMS breadcrumbs field (unlike PageResource), so the
  // "Blogs" ancestor label/url matches the listing page's own breadcrumb
  // (pages/blogs/index.vue) rather than being re-derived here.
  const breadcrumbItems = [
    { name: "Home", url: `${siteUrl}/` },
    { name: "Blog", url: `${siteUrl}/${route.path.split("/")[1]}/blogs` },
    { name: post.title, url: pageUrl.value },
  ];

  const scripts = [
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify(useBreadcrumbSchema(breadcrumbItems, pageUrl.value)),
    },
  ];

  // datetime is "YYYYMMDD" (e.g. "20250711") from posts.date via the CMS —
  // reformat to the ISO 8601 date schema.org requires for datePublished.
  const datetime = post.datetime;
  const isoDate = /^\d{8}$/.test(datetime || "")
    ? `${datetime.slice(0, 4)}-${datetime.slice(4, 6)}-${datetime.slice(6, 8)}`
    : null;

  scripts.push({
    type: "application/ld+json",
    innerHTML: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Article",
      "@id": `${pageUrl.value}#article`,
      headline: post.title,
      url: pageUrl.value,
      ...(isoDate ? { datePublished: isoDate } : {}),
      author: { "@id": `${siteUrl}/#organization` },
      publisher: { "@id": `${siteUrl}/#organization` },
      mainEntityOfPage: pageUrl.value,
    }),
  });

  return { script: scripts };
});

// Inject custom head/footer client-side only (touches document, unsafe during SSR)
onMounted(() => {
  watch(
    () => [custom_head.value, custom_footer.value],
    ([head, footer]) => {
      if (head) injectCustomHead(head);
      if (footer) injectCustomFooter(footer);
    },
    { immediate: true }
  );
});

const showImg = (i) => {
  indexRef.value = i;
  visibleRef.value = true;
};

const onHide = () => (visibleRef.value = false);

</script>

<style lang="scss" scoped>
@import "@/assets/scss/app";

@each $lang in $languages {
  html[lang="#{$lang}"] {
    .block {
      &.block--blog {
        .block__title {
          @include font-weight("bold", $lang);
          line-height: 1.07;
        }

        .block__date {
          @include font-size("body-2", $lang);
          @include font-weight("bolder", $lang);
        }

        .block__subtitle {
          @include font-size("lead", $lang);
          @include font-weight("semibold", $lang);
        }

        .block__content {
          line-height: 1.3;
        }

        .block__detail {
          line-height: 1.5;
        }
      }
    }
  }
}

.block {
  &.block--blog {
    .block__date {
      color: $yellow-ci;
    }

    .block__title {
      margin: 22px 0 68px 0;
      color: $black;
      @include media-breakpoint-down(sm) {
        margin: 22px 0 40px 0;
      }
    }

    .block__subtitle {
      color: $black-4;
      margin: 40px 0 0 0;
    }

    .block__image {
      text-align: center;
      img {
        width: 100%;
        max-width: 800px;
        height: auto;
        object-fit: cover;
        object-position: center;
        border-radius: 40px;
      }
    }

    .block__content {
      margin: 5.688rem 0 3rem 0;

      @include media-breakpoint-down(sm) {
        margin: 3.125rem 0 3rem 0;
      }

      .small {
        color: $black-4;
        margin: 40px 0 0 0;
      }
    }

    .block__detail {
      margin: 12px 0 0 0;
    }

    .block__gallery {
      margin: 4.375rem 0 0 0;

      @include media-breakpoint-down(md) {
        overflow: hidden;
      }
    }
  }
}

.social-share-wrapper {
  position: fixed;
  top: 224px;
  right: 35px;
  z-index: 1;
  @include media-breakpoint-down(md) {
    right: 20px;
  }
}


</style>